/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import roteNdaoGeo from "../../../assets/jsonData/kab_Rote.json";

// Data

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MapContainer, TileLayer, GeoJSON, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { API_HOHOLOK } from "utils";

function formatTanggalIndo(dateStr) {
  if (!dateStr) return "-";
  const bulan = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  return `${d.getDate()} ${bulan[d.getMonth()]} ${d.getFullYear()}`;
}

function Map() {
  const [chartData, setChartData] = useState(null);
  const [proyekList, setProyekList] = useState([]);
  const [selectedProyek, setSelectedProyek] = useState(null);
  const [tahun, setTahun] = useState(2025);
  const [loading, setLoading] = useState(false);
  const [laporanPerTanggal, setLaporanPerTanggal] = useState([]);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewList, setPreviewList] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [mouseLatLng, setMouseLatLng] = useState(null);
  const mapRef = useRef();

  // Fetch daftar proyek
  useEffect(() => {
    setLoading(true);
    axios.get(`${API_HOHOLOK}proyek`).then((res) => {
      if (Array.isArray(res.data)) {
        setProyekList(
          res.data.map((p) => ({
            ...p,
            label: p.nama_proyek,
            value: p.id_proyek,
          }))
        );
      }
      setLoading(false);
    });
  }, []);

  // Fetch data chart sesuai proyek & tahun
  useEffect(() => {
    if (!selectedProyek) return;
    axios
      .get(
        `${API_HOHOLOK}laporan-harian/proyek/${selectedProyek.value}?tahun=${tahun}`
      )
      .then((res) => {
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          const data = res.data.data;
          // Kelompokkan per tanggal (YYYY-MM-DD)
          const group = {};
          data.forEach((item) => {
            const tgl = new Date(item.tanggal).toISOString().slice(0, 10);
            if (!group[tgl]) group[tgl] = [];
            group[tgl].push(item);
          });
          // Ubah ke array dan urutkan tanggal terbaru ke atas
          const arr = Object.entries(group)
            .map(([tanggal, items]) => ({ tanggal, items }))
            .sort((a, b) => b.tanggal.localeCompare(a.tanggal));
          setLaporanPerTanggal(arr);
        }
      });
  }, [selectedProyek, tahun]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox mb={2} display="flex" alignItems="center" gap={2}>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}></Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <Autocomplete
                    options={[2024, 2025, 2026, 2027, 2028, 2029]}
                    value={tahun}
                    onChange={(event, newValue) => {
                      setTahun(newValue);
                    }}
                    getOptionLabel={(option) => option.toString()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tahun"
                        variant="standard"
                        fullWidth
                      />
                    )}
                    sx={{ minWidth: 120 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </MDBox>

        <MDBox mt={2} mb={4}>
          <MapContainer
            style={{ height: "500px", width: "100%" }}
            center={[-10.7116, 123.0667]}
            zoom={10}
            scrollWheelZoom={true}
            whenCreated={(mapInstance) => {
              mapRef.current = mapInstance;
              mapInstance.on("mousemove", function (e) {
                setMouseLatLng({ lat: e.latlng.lat, lng: e.latlng.lng });
              });
              mapInstance.on("mouseout", function () {
                setMouseLatLng(null);
              });
            }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <GeoJSON data={roteNdaoGeo} />
            {proyekList
              .filter((p) => p.latitude && p.longitude)
              .map((p, idx) => (
                <Marker
                  key={p.id_proyek || idx}
                  position={[parseFloat(p.latitude), parseFloat(p.longitude)]}
                  icon={L.icon({
                    iconUrl:
                      "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowUrl:
                      "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
                    shadowSize: [41, 41],
                  })}
                >
                  <Popup>
                    <div>
                      <strong>{p.nama_proyek}</strong>
                      <br />
                      Nilai:{" "}
                      {p.total_anggaran
                        ? Number(p.total_anggaran).toLocaleString("id-ID")
                        : "-"}
                      <br />
                      Lokasi: {p.lokasi || "-"}
                      <br />
                      Tanggal Mulai:{" "}
                      {p.tanggal_mulai
                        ? formatTanggalIndo(p.tanggal_mulai)
                        : "-"}
                      <br />
                      Tanggal Selesai:{" "}
                      {p.tanggal_selesai
                        ? formatTanggalIndo(p.tanggal_selesai)
                        : "-"}
                    </div>
                  </Popup>
                </Marker>
              ))}
          </MapContainer>
          <MDBox mt={1} textAlign="right" color="text.secondary" fontSize={14}>
            {mouseLatLng ? (
              <span>
                Koordinat: {mouseLatLng.lat.toFixed(6)},{" "}
                {mouseLatLng.lng.toFixed(6)}
              </span>
            ) : (
              <span>&nbsp;</span>
            )}
          </MDBox>
        </MDBox>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default Map;
